import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, ThemeProviderV2 } from '@blueprint/ui';
import { useHistory } from "react-router-dom";
import projectApi from "../../../api/project";
import { Loader, toast, toastError } from "../../../common/components/core";
import {
    useAuthErrorHandlerCreator,
    useModalWithData,
} from "../../../common/hooks";
import {
    composeErrorHandlers,
    GenericError,
} from "../../../utils/errorHandling";
import { ProjectFormValues } from "../components/ProjectForm";
import { isApiError } from "../../../api/common";

type EditProjectHandler = {
    isSubmitting: boolean;
    handler: (formValues: ProjectFormValues) => void;
};

const useEditProjectHandler = (projectRef: string): EditProjectHandler => {
    const history = useHistory();
    const [isSubmitting, setSubmitting] = useState(false);
    const onAuthError = useAuthErrorHandlerCreator();
    const [show, hide] = useModalWithData<ProjectFormValues>(
        (formValues) => () => {
            const confirm = async () => {
                setSubmitting(true);
                try {
                    if (!formValues) {
                        throw new Error("invalid form values");
                    }
                    // TODO API in progress
                    await projectApi.update(projectRef, formValues);
                    toast(`Project '${formValues.name}' updated`);
                    history.push("/project");
                } catch (e) {
                    setSubmitting(false);
                    composeErrorHandlers(onAuthError, (next) => (err) => {
                        if (
                            isApiError(err) &&
                            err.errors[0]?.key ===
                            "Corda.Verification.ContractReject"
                        ) {
                            toastError(
                                "Error removing approver because this approver was the only one assigned to a submitted claim.",
                            );
                        } else if (
                            isApiError(err) &&
                            err.errors[0]?.key === "Evm.Revert" && err.errors[0]?.enMessage?.includes("DuplicateProjectId") ){
                            toastError(
                                "Project ID already exists.",
                            );
                        } else {
                            next(err);
                        }
                    })(e as GenericError);
                }
            };

            return (
                <ThemeProviderV2>
                <Modal show onHide={hide} backdrop="static" centered>
                    <Loader overlay isLoading={isSubmitting}>
                        <Modal.Header>
                            <h2 className="mb-0">
                                Update Project Folder Details
                            </h2>
                        </Modal.Header>
                        <Modal.Body>
                            <p>
                                Are you sure you want to update the project
                                folder details?
                            </p>

                            <div className="text-primary">IMPORTANT:</div>
                            <p style={{ fontSize: "10px" }}>
                                Changing the project folder details will not
                                affect any previously created claim/invoice
                                submissions.
                            </p>
                        </Modal.Body>

                        <Modal.Footer className="d-flex justify-content-between px-3">
                            <Button variant="light.tertiary" onClick={hide}>
                                Cancel
                            </Button>
                            <Button variant="light.primary" onClick={confirm}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Loader>
                </Modal>
                </ThemeProviderV2>
            );
        },
        [isSubmitting, onAuthError],
    );

    return {
        isSubmitting,
        handler: show,
    };
};

export default useEditProjectHandler;
